import Cookies from 'js-cookie'

/**
 * Google Ads click identifier capture (P0-1).
 *
 * When a user lands on any page with a Google Ads click identifier in the URL
 * (`gclid`), we persist it in a long-lived first-party cookie so the value
 * survives until the user eventually converts. The value is stored verbatim
 * (case-sensitive, never hashed) because Google Ads matches on the exact click
 * identifier.
 *
 * - `gclid` — standard Google click identifier (google_click_id)
 *
 * Downstream (P0-2) reads this via `readGoogleClickId()` at contract creation
 * time to stamp the identifier onto the conversion-creating transaction.
 */

// Cookie name — underscore-prefixed to denote first-party/internal.
export const GOOGLE_CLICK_ID_COOKIE = '_adc_google_click_id'

// 90-day TTL. Google Ads' default click-conversion window is 90 days, so a
// longer cookie life would retain identifiers that can no longer be attributed.
const COOKIE_TTL_DAYS = 90

// Ordered so callers can iterate URL param -> cookie name pairs.
// Note: URL params use Google's standard short names (gclid).
const IDENTIFIERS = [
  { param: 'gclid', cookie: GOOGLE_CLICK_ID_COOKIE },
] as const

export interface GoogleClickIdentifiers {
  google_click_id: string | null
}

/**
 * Case-sensitive URL query parser. Unlike the shared `getUrlVars` helper (which
 * lowercases keys), we must preserve the exact case of both the parameter name
 * and its value — Google click identifiers are case-sensitive.
 */
function getSearchParams(search: string): URLSearchParams | null {
  if (typeof search !== 'string' || search.length === 0) {
    return null
  }
  try {
    return new URLSearchParams(
      search.startsWith('?') ? search.slice(1) : search
    )
  } catch {
    return null
  }
}

/**
 * Read the current Google click identifier from cookies. Returns `null` when
 * not present. Safe to call in non-browser contexts.
 */
export function readGoogleClickId(): GoogleClickIdentifiers {
  return {
    google_click_id: Cookies.get(GOOGLE_CLICK_ID_COOKIE) ?? null,
  }
}

/**
 * Capture the Google click identifier from the current URL and persist it in a
 * first-party cookie with a 90-day TTL.
 *
 * - Only writes a cookie for an identifier that is present in the URL; normal
 *   navigation (no identifier in the URL) never clears an existing cookie.
 * - A new arrival carrying a new value overwrites the existing cookie.
 * - Idempotent and safe to call multiple times per page load.
 *
 * @returns the identifier now held in cookies (post-capture).
 */
export function captureGoogleClickId(): GoogleClickIdentifiers {
  if (typeof window === 'undefined') {
    return { google_click_id: null }
  }

  const params = getSearchParams(window.location?.search ?? '')
  if (params) {
    for (const { param, cookie } of IDENTIFIERS) {
      const value = params.get(param)
      // Ignore empty values (e.g. `?gclid=`) — they carry no attribution.
      if (value) {
        Cookies.set(cookie, value, {
          expires: COOKIE_TTL_DAYS,
          path: '/',
          sameSite: 'Lax',
        })
      }
    }
  }

  return readGoogleClickId()
}
